import React, { useState, useEffect } from 'react';
import { CheckCircle, FileText, Calendar, Clock, Search, Filter, Eye, BarChart3, Award } from 'lucide-react';
import { superAdminAPI } from '../../services/api';
import { CardLoadingSpinner } from '../ui/LoadingSpinner';
import { showSuccessToast, showErrorToast } from '../../utils/errorHandling';

const CompletedReviews = () => {
  const [completedReviews, setCompletedReviews] = useState([]);
  const [stats, setStats] = useState({
    totalReviews: 0,
    totalQuestions: 0,
    averageTime: 0,
    approvalRate: 0
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('30d');

  useEffect(() => {
    loadCompletedReviews();
  }, [selectedPeriod]);

  const loadCompletedReviews = async () => {
    try {
      setLoading(true);
      // For now, we'll show placeholder data since the API might not be implemented yet
      setStats({
        totalReviews: 15,
        totalQuestions: 187,
        averageTime: 2.3,
        approvalRate: 89.3
      });

      setCompletedReviews([
        {
          id: '1',
          title: 'Mathematics Foundation Review',
          kitName: 'Mathematics Foundation',
          questionCount: 25,
          approvedCount: 23,
          rejectedCount: 2,
          completedAt: '2024-01-15T16:30:00Z',
          timeSpent: 6.5,
          assignedBy: 'Content Supervisor',
          feedback: 'Excellent review quality. Minor corrections needed on 2 questions.',
          rating: 4.8
        },
        {
          id: '2',
          title: 'Physics Mechanics Review',
          kitName: 'Physics Fundamentals',
          questionCount: 18,
          approvedCount: 16,
          rejectedCount: 2,
          completedAt: '2024-01-12T14:20:00Z',
          timeSpent: 4.2,
          assignedBy: 'Content Supervisor',
          feedback: 'Good attention to detail. Provided helpful feedback on rejected questions.',
          rating: 4.5
        },
        {
          id: '3',
          title: 'Chemistry Basics Review',
          kitName: 'Chemistry Essentials',
          questionCount: 12,
          approvedCount: 12,
          rejectedCount: 0,
          completedAt: '2024-01-10T11:45:00Z',
          timeSpent: 2.8,
          assignedBy: 'Content Supervisor',
          feedback: 'Perfect review! All questions approved with thorough analysis.',
          rating: 5.0
        }
      ]);
    } catch (error) {
      console.error('Error loading completed reviews:', error);
      showErrorToast(error, 'Failed to load completed reviews');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (review) => {
    showSuccessToast(`Viewing details for: ${review.title}`);
    // Open review details modal
  };

  const getApprovalRate = (approved, total) => {
    return total > 0 ? Math.round((approved / total) * 100) : 0;
  };

  const getRatingStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push('★');
    }
    if (hasHalfStar) {
      stars.push('☆');
    }
    while (stars.length < 5) {
      stars.push('☆');
    }
    return stars.join('');
  };

  const filteredReviews = completedReviews.filter(review =>
    review.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    review.kitName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const periodOptions = [
    { value: '7d', label: 'Last 7 Days' },
    { value: '30d', label: 'Last 30 Days' },
    { value: '90d', label: 'Last 90 Days' },
    { value: '1y', label: 'Last Year' }
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Completed Reviews</h1>
          <p className="text-gray-600 mt-1">Track your review history and performance metrics</p>
        </div>
        <select
          value={selectedPeriod}
          onChange={(e) => setSelectedPeriod(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          {periodOptions.map(option => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-4">
              <div className="text-2xl font-bold text-gray-900">{stats.totalReviews}</div>
              <div className="text-sm text-gray-600">Total Reviews</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <FileText className="h-8 w-8 text-blue-600" />
            </div>
            <div className="ml-4">
              <div className="text-2xl font-bold text-gray-900">{stats.totalQuestions}</div>
              <div className="text-sm text-gray-600">Questions Reviewed</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Clock className="h-8 w-8 text-yellow-600" />
            </div>
            <div className="ml-4">
              <div className="text-2xl font-bold text-gray-900">{stats.averageTime}h</div>
              <div className="text-sm text-gray-600">Avg Review Time</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <BarChart3 className="h-8 w-8 text-purple-600" />
            </div>
            <div className="ml-4">
              <div className="text-2xl font-bold text-gray-900">{stats.approvalRate}%</div>
              <div className="text-sm text-gray-600">Approval Rate</div>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <input
            type="text"
            placeholder="Search completed reviews by title or KIT name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      {/* Reviews List */}
      <div className="space-y-4">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <CardLoadingSpinner />
          </div>
        ) : filteredReviews.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
            <CheckCircle className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No completed reviews found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm
                ? 'Try adjusting your search criteria.'
                : 'You haven\'t completed any reviews in the selected period.'}
            </p>
          </div>
        ) : (
          filteredReviews.map((review) => {
            const approvalRate = getApprovalRate(review.approvedCount, review.questionCount);
            return (
              <div key={review.id} className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-medium text-gray-900">{review.title}</h3>
                      <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Completed
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                      <div className="flex items-center text-sm text-gray-600">
                        <FileText className="h-4 w-4 mr-2" />
                        <span>{review.kitName}</span>
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <Calendar className="h-4 w-4 mr-2" />
                        <span>{new Date(review.completedAt).toLocaleDateString()}</span>
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <Clock className="h-4 w-4 mr-2" />
                        <span>{review.timeSpent}h spent</span>
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <Award className="h-4 w-4 mr-2" />
                        <span>{getRatingStars(review.rating)} ({review.rating})</span>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div className="bg-gray-50 rounded-lg p-3">
                        <div className="text-sm text-gray-600">Total Questions</div>
                        <div className="text-lg font-semibold text-gray-900">{review.questionCount}</div>
                      </div>
                      <div className="bg-green-50 rounded-lg p-3">
                        <div className="text-sm text-green-600">Approved</div>
                        <div className="text-lg font-semibold text-green-900">{review.approvedCount}</div>
                      </div>
                      <div className="bg-red-50 rounded-lg p-3">
                        <div className="text-sm text-red-600">Rejected</div>
                        <div className="text-lg font-semibold text-red-900">{review.rejectedCount}</div>
                      </div>
                    </div>

                    <div className="mb-4">
                      <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                        <span>Approval Rate</span>
                        <span>{approvalRate}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-green-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${approvalRate}%` }}
                        ></div>
                      </div>
                    </div>

                    {review.feedback && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                        <div className="text-sm font-medium text-blue-900 mb-1">Supervisor Feedback</div>
                        <div className="text-sm text-blue-800">{review.feedback}</div>
                      </div>
                    )}
                  </div>

                  <div className="ml-4">
                    <button
                      onClick={() => handleViewDetails(review)}
                      className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors flex items-center"
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default CompletedReviews;
